/* Grace Collection configurator (templates/product.grace + sections/product-grace).
   Contract:
   - Priced choices are real variant options. The JS resolves a variant by matching EVERY option
     name -> value exactly (no substring matching), so options can be added freely.
   - The personalization fee (where one exists) is a variant option named in GC_TYPES[type].pers.opt
     with values "No" / "Yes".
   - Free choices go to line-item properties.
   - Leather color: if the product has a Color option with 2+ values, swatches pick the variant.
     Otherwise all 17 leathers are offered as a property, with the pictured color first.
   - Name designs (Dog Breed world): when window.GC_META.kind === 'name', a required name field sits at the
     top of the options. The typed name replaces GC_META.sampleName on the front, above GC_META.secondLine.
     A live fob preview shows the lettering. Properties: "Dog's Name", "Front Design". Without kind, nothing changes.
   - Extra field (occasion designs): when window.GC_META.extraLabel is set, a second required input sits under the
     name (or alone, when kind is absent). GC_META.extraKind = year | date | number | text sets the validation:
     year = 4 digits, 1900 to the current year; date = M·D·YY (slashes or periods typed are turned into middle dots,
     a 4-digit year is shortened); number = 1 to 3 digits; text = letters and spaces up to GC_META.extraMax.
     GC_META.extraSample is the value shown on the photos. The value travels as a property named by extraLabel.
     GC_META.frontTemplate (e.g. "{name} / NOEL · {extra}") builds "Front Design" and the fob preview: " / " splits
     lines, the first line previews in script, the rest in caps; {name} = name as typed, {NAME} = name in capitals.
     GC_META.nameRequired === false makes the name optional. Without these keys, nothing changes. */
(function () {
  'use strict';

  var LEATHERS = [
    ['arizona', 'Arizona'], ['sicilian-brown', 'Sicilian Brown'], ['caramel', 'Caramel'],
    ['dark-espresso', 'Dark Espresso'], ['black', 'Black'], ['blueberry', 'Blueberry'],
    ['forest-green', 'Forest Green'], ['indian-summer', 'Indian Summer'], ['gray-sand', 'Gray Sand'],
    ['country-grey', 'Country Gray'], ['turquoise', 'Turquoise'], ['blush', 'Blush'],
    ['parisian-blossom', 'Parisian Blossom'], ['lilac', 'Lilac'], ['fuchsia', 'Fuchsia'],
    ['poppy-red', 'Poppy Red'], ['sangria', 'Sangria']
  ];

  var IPHONES = ['iPhone 12', 'iPhone 12 Pro', 'iPhone 12 Pro Max', 'iPhone 13', 'iPhone 13 mini', 'iPhone 13 Pro',
    'iPhone 13 Pro Max', 'iPhone 14', 'iPhone 14 Plus', 'iPhone 14 Pro', 'iPhone 14 Pro Max', 'iPhone 15',
    'iPhone 15 Plus', 'iPhone 15 Pro', 'iPhone 15 Pro Max', 'iPhone 16', 'iPhone 16 Plus', 'iPhone 16 Pro',
    'iPhone 16 Pro Max', 'iPhone 17', 'iPhone Air', 'iPhone 17 Pro', 'iPhone 17 Pro Max', 'iPhone 18', 'iPhone 18 Pro',
    'iPhone 18 Pro Max'];

  // Where each product can take a personal stamp without crowding the verse.
  // pers.opt = variant option carrying a fee (No/Yes); absent = free.
  var GC_TYPES = {
    'Leather Journal': {
      paper: true,
      optInfo: { Size: { Small: '4⅜ × 6³⁄₁₆ in, pocket size', Medium: '5⁷⁄₁₆ × 7⅞ in, desk size' } },
      pers: { opt: 'Inside Personalization', title: 'Personalize the inside', where: 'Inside front cover', max: 24,
        hint: 'The verse keeps the cover. A name, initials, a date or a short dedication is pressed into the leather inside the front cover.',
        ph: 'Norma · EYK · Easter 2026' }
    },
    "Men's Wallet": {
      pers: { title: 'Initials inside', where: 'Inner panel', max: 10,
        hint: 'Pressed into the inner panel, where only the owner will see it. Initials or a date fit best.',
        ph: 'RMK · J♥D' }
    },
    'Passport Cover': {
      optInfo: { Style: { 'Passport Cover': 'A slim cover for the passport alone', 'Travel Wallet': 'Adds pockets for cards and a boarding pass' } },
      pers: { opt: 'Back Personalization', title: 'Personalize the back cover', where: 'Back cover', max: 16,
        hint: 'The verse holds the front. A name or initials go on the back cover.',
        ph: 'Hartley · A.M.R.' }
    },
    // Keychains can be stamped on one side only (the front carries the design) -> no personalization.
    'Keychain': {},
    'Luggage Tag': {
      pers: { title: 'Name on the back', where: 'Back side', max: 16,
        hint: 'The verse sits on the front. A name on the back makes the bag easy to claim.',
        ph: 'The Hartleys' }
    },
    'Camera Strap': {
      req: [
        { k: 'Strap Length', vals: ['Standard', 'Long'] },
        { k: 'Attachment', vals: ['Belt', 'Snap hook'] }
      ],
      pers: { opt: 'Name Stamp', title: 'Name on the other end', where: 'Opposite end of the strap', max: 14,
        hint: 'The verse sits on one end of the strap. A name or initials can go on the other.',
        ph: 'Hannah' }
    },
    'iPhone Case': { req: [{ k: 'iPhone Model', select: IPHONES }] },
    'Dopp Kit': { optInfo: { Size: { Compact: 'Toothbrush and razor', Standard: 'The everyday travel size', Large: 'Room for a full kit' } } },
    'Makeup Bag': { optInfo: { Size: { Standard: 'Everyday essentials', Large: 'Room for brushes and palettes' } } },
    'Tote Bag': {
      optInfo: { Size: { Small: 'Books and a laptop sleeve', Medium: 'The everyday carry', Large: 'Weekend and market runs', 'Extra Large': 'Travel and overnight' },
        Zipper: { No: 'Open top', Yes: 'Zip closure across the top' } }
    },
    'Jewelry Box': {},
    'Coaster Set': {},
    'Money Clip': {}
  };

  var EM = 'leatherius.woodman@gmail.com';

  function init() {
    var P = window.GC_PRODUCT, root = document.getElementById('gc-configurator');
    if (!P || !root || root.getAttribute('data-ready')) return;
    root.setAttribute('data-ready', '1');

    var W = window.GC_WORLD || {};
    var T = GC_TYPES[P.type] || {};
    var TO = (W.types || {})[P.type];
    if (TO) {
      T = JSON.parse(JSON.stringify(T));
      Object.keys(TO).forEach(function (k) { T[k] = (k === 'pers' && T.pers && TO.pers) ? Object.assign({}, T.pers, TO.pers) : TO[k]; });
    }
    var opts = P.options.map(function (o) { return typeof o === 'string' ? o : o.name; });
    var colorIdx = opts.indexOf('Color');
    var persOpt = T.pers && T.pers.opt && opts.indexOf(T.pers.opt) !== -1 ? T.pers.opt : null;

    function vals(i) {
      var seen = [], k = 'option' + (i + 1);
      P.variants.forEach(function (v) { if (seen.indexOf(v[k]) === -1) seen.push(v[k]); });
      return seen;
    }
    var colorVals = colorIdx !== -1 ? vals(colorIdx) : [];
    var colorIsVariant = colorVals.length > 1;
    var pictured = colorVals.length === 1 ? colorVals[0] : null;

    // Options the shopper picks as cards (priced axes). Color and the personalization flag are handled separately.
    var cardOpts = [];
    opts.forEach(function (n, i) {
      if (i === colorIdx || n === persOpt || n === 'Title') return;
      cardOpts.push({ name: n, idx: i, values: vals(i) });
    });

    // Name designs: the buyer's name replaces the sample name on the front (one-sided pieces such as keychains).
    var META = window.GC_META || {};
    var NM = META.kind === 'name';
    var NAME_MAX = Math.min(Math.max(+(W.nameMax) || 10, 3), 16);
    var sampleName = String(META.sampleName || '').trim();
    var secondLine = String(META.secondLine || '').trim();

    // Extra field + front template (occasion designs). All behind EX / TPL / NAME_OPT; absent keys = old page.
    var EX = !!String(META.extraLabel || '').trim();
    var NAME_OPT = NM && META.nameRequired === false;
    var TPL = (NM || EX) && String(META.frontTemplate || '').trim() ? String(META.frontTemplate).trim() : '';
    var X = null;
    if (EX) {
      var xk = String(META.extraKind || 'text').toLowerCase();
      if (['year', 'date', 'number', 'text'].indexOf(xk) === -1) xk = 'text';
      var xm = parseInt(META.extraMax, 10);
      var XDEF = { year: 4, date: 10, number: 3, text: 12 };
      X = {
        label: String(META.extraLabel).trim(), kind: xk, sample: String(META.extraSample || '').trim(),
        max: xk === 'number' ? Math.min(3, xm > 0 ? xm : 3) : xk === 'text' ? (xm > 0 ? xm : XDEF.text) : XDEF[xk],
        nowYear: new Date().getFullYear()
      };
    }

    var start = P.variants.filter(function (v) { return v.id === (window.GC_META || {}).selectedVariant; })[0] || P.variants[0];
    var S = {
      opt: {}, color: colorIsVariant ? start['option' + (colorIdx + 1)] : (pictured || 'As pictured'),
      finish: 'As pictured', paper: 'Lined', req: {}, persOn: false, text: '', font: 'Straight', deadline: '',
      dogName: ''
    };
    if (EX) S.extra = '';
    cardOpts.forEach(function (o) { S.opt[o.name] = start['option' + (o.idx + 1)]; });
    (T.req || []).forEach(function (r) { S.req[r.k] = r.select ? '' : r.vals[0]; });

    function money(c) { return '$' + (c / 100).toFixed(2); }
    function esc(s) { return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); }
    function slug(n) { var s = n.toLowerCase().replace(/\s+/g, '-'); return s === 'country-gray' ? 'country-grey' : s; }
    function swatch(n) { var L = window.GC_LEATHER || {}; return L[slug(n)] || ''; }

    function wanted(over) {
      var w = [];
      opts.forEach(function (n, i) {
        var v;
        if (i === colorIdx) v = colorIsVariant ? S.color : colorVals[0];
        else if (n === persOpt) v = S.persOn ? 'Yes' : 'No';
        else if (n === 'Title') v = P.variants[0]['option' + (i + 1)];
        else v = S.opt[n];
        if (over && over[n] !== undefined) v = over[n];
        w.push(v);
      });
      return w;
    }
    function findVariant(over) {
      var w = wanted(over);
      for (var i = 0; i < P.variants.length; i++) {
        var v = P.variants[i], ok = true;
        for (var j = 0; j < w.length; j++) if (v['option' + (j + 1)] !== w[j]) { ok = false; break; }
        if (ok) return v;
      }
      return null;
    }

    // ---------- render ----------
    function head() {
      return '<div class="gc-price-row"><span class="gc-price" id="gc-price"></span>' +
        '<span class="gc-perks">Free US shipping · 10% off in cart</span></div>';
    }

    // ---------- name designs ----------
    // Letters (Latin incl. accents), spaces, apostrophe, hyphen. Curly apostrophes are straightened.
    var NAME_BAD = /[^A-Za-z\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u024F' -]/g;
    function cleanName(raw) {
      var v = String(raw || '').replace(/[\u2018\u2019\u02BC`]/g, "'").replace(/[\u2010-\u2014]/g, '-').replace(NAME_BAD, '');
      v = v.replace(/^[\s'-]+/, '').replace(/ {2,}/g, ' ').replace(/'{2,}/g, "'").replace(/-{2,}/g, '-');
      if (v.length > NAME_MAX) v = v.slice(0, NAME_MAX);
      return v.charAt(0).toUpperCase() + v.slice(1);
    }
    function nameOk(n) { return /^[A-Za-z\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u024F][A-Za-z\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u024F' -]*$/.test(n) && n.length <= NAME_MAX; }

    function blkName() {
      var help = 'It replaces ' + (sampleName || 'the sample name') + ' on the front.';
      return '<div class="gc-blk gc-name" id="gc-name">' +
        '<label class="gc-lbl" for="gc-dogname">' + esc(W.nameLabel || 'Your dog\'s name') + ' <span class="gc-req">required</span></label>' +
        '<div class="gc-name-row"><div class="gc-name-f">' +
        '<div class="gc-inp-row"><input class="gc-inp" id="gc-dogname" name="gc-dogname" maxlength="' + NAME_MAX + '" autocomplete="off" autocapitalize="words" spellcheck="false"' +
        ' placeholder="Type the name" value="' + esc(S.dogName) + '" aria-required="true" aria-describedby="gc-name-help">' +
        '<span class="gc-count" id="gc-name-count"></span></div>' +
        '<p class="gc-note" id="gc-name-help">' + esc(help) + '</p>' +
        '<p class="gc-name-rule">Up to ' + NAME_MAX + ' letters. Spaces, apostrophes and hyphens are fine.</p></div>' +
        '<div class="gc-fob-w" aria-hidden="true"><div class="gc-fob" id="gc-fob"><span class="gc-fob-hole"></span>' +
        '<span class="gc-fob-n" id="gc-fob-n"></span>' + (secondLine ? '<span class="gc-fob-l" id="gc-fob-l">' + esc(secondLine) + '</span>' : '') +
        '</div><p class="gc-prev-c">Front lettering</p></div></div></div>';
    }

    // Shrink the name until it fits the fob (10 characters of script is wide).
    function fitName(el) {
      var size = 30; el.style.fontSize = size + 'px';
      while (size > 15 && el.scrollWidth > el.clientWidth + 1) { size -= 1; el.style.fontSize = size + 'px'; }
    }

    function updateName() {
      var fob = root.querySelector('#gc-fob'); if (!fob) return;
      var n = root.querySelector('#gc-fob-n'), l = root.querySelector('#gc-fob-l');
      var sw = swatch(S.color) || swatch(pictured || '') || swatch('Arizona');
      fob.style.backgroundImage = sw ? 'url(' + sw + ')' : '';
      var gold = S.finish === 'Gold foil' ? ' gold' : '';
      n.textContent = S.dogName || sampleName || 'Name';
      n.className = 'gc-fob-n' + (S.dogName ? '' : ' gc-prev-ph') + gold;
      if (l) l.className = 'gc-fob-l' + gold;
      fitName(n);
      root.querySelector('#gc-name-count').textContent = S.dogName.length + '/' + NAME_MAX;
    }

    // ---------- extra field + front template (occasion designs) ----------
    // XM = any of the new keys present. When false, render/update/add use the untouched code above.
    var XM = EX || NAME_OPT || !!TPL;
    var LETTER = 'A-Za-zÀ-ÖØ-öø-ɏ';

    function cleanExtra(raw) {
      var v = String(raw || '');
      if (X.kind === 'year') return v.replace(/\D/g, '').slice(0, 4);
      if (X.kind === 'number') return v.replace(/\D/g, '').slice(0, X.max);
      if (X.kind === 'date') {
        v = v.replace(/[\/.\-•∙⋅・]/g, '·').replace(/[^\d·]/g, '')
          .replace(/·{2,}/g, '·').replace(/^·/, '');
        var parts = v.split('·');
        if (parts.length > 3) v = parts.slice(0, 3).join('·');
        return v.slice(0, X.max);
      }
      v = v.replace(/[\/•∙⋅・]/g, '·').replace(new RegExp('[^' + LETTER + "0-9 ·.&'\\-]", 'g'), '').replace(/^[ ·]+/, '').replace(/ {2,}/g, ' ');
      return v.slice(0, X.max);
    }
    // Two-digit years read as 1990-1999 (90-99) or 2000 to this year; later two-digit years are refused.
    function normDate(v) {
      var m = /^(\d{1,2})·(\d{1,2})·(\d{2}|\d{4})$/.exec(v); if (!m) return '';
      var mo = +m[1], d = +m[2], y = m[3], cy = X.nowYear % 100, fy;
      if (y.length === 4) { fy = +y; if (fy < 1990 || fy > X.nowYear) return ''; }
      else { fy = +y >= 90 ? 1900 + +y : 2000 + +y; if (+y > cy && +y < 90) return ''; }
      if (mo < 1 || mo > 12 || d < 1 || d > new Date(fy, mo, 0).getDate()) return '';
      return mo + '·' + d + '·' + String(fy % 100).replace(/^(\d)$/, '0$1');
    }
    // The value that goes to the cart, or '' when it fails the kind's rule.
    function finalExtra(raw) {
      var v = cleanExtra(raw).replace(/[ ·]+$/, '');
      if (X.kind === 'year') return /^\d{4}$/.test(v) && +v >= 1900 && +v <= X.nowYear ? v : '';
      if (X.kind === 'number') return /^\d{1,3}$/.test(v) && v.length <= X.max ? v : '';
      if (X.kind === 'date') return normDate(v);
      return new RegExp('[' + LETTER + '0-9]').test(v) && v.length <= X.max ? v : '';
    }
    function extraRule() {
      if (X.kind === 'year') return 'Four digits, 1900 to ' + X.nowYear + '.';
      if (X.kind === 'date') return 'Month·day·year, like 3·14·24. Slashes work too.';
      if (X.kind === 'number') return X.max === 1 ? 'One digit.' : 'Up to ' + X.max + ' digits.';
      return 'Up to ' + X.max + ' letters, numbers and spaces.';
    }
    function extraErr() {
      if (X.kind === 'year') return X.label + ': type a year from 1900 to ' + X.nowYear + '.';
      if (X.kind === 'date') return X.label + ': type the date as month·day·year, like 3·14·24.';
      if (X.kind === 'number') return X.label + ': type ' + (X.max === 1 ? 'one digit.' : 'up to ' + X.max + ' digits.');
      return X.label + ': type it in letters, numbers and spaces, up to ' + X.max + '.';
    }
    function extraSample() {
      if (X.sample) return X.sample;
      return X.kind === 'year' ? String(X.nowYear) : X.kind === 'date' ? '3·14·24' : X.kind === 'number' ? '7' : 'Text';
    }

    // Template: " / " splits lines; tokens {name}, {NAME}, {extra}. Without a template the lines are
    // name, secondLine, extra (whichever exist).
    function tplText() {
      return TPL || [NM ? '{name}' : '', secondLine, EX ? '{extra}' : ''].filter(Boolean).join(' / ');
    }
    function tidyLine(s) { return s.replace(/\s{2,}/g, ' ').replace(/^[\s·]+|[\s·]+$/g, ''); }
    function frontDesign(name, extra) {
      return tplText().split(' / ').map(function (line) {
        return tidyLine(line.replace(/\{(name|NAME|extra)\}/g, function (m, k) {
          return k === 'extra' ? extra : k === 'NAME' ? name.toUpperCase() : name;
        }));
      }).filter(Boolean).join(' / ');
    }

    function blkX() {
      var h = '<div class="gc-blk gc-name gc-name-x" id="gc-name"><div class="gc-name-row"><div class="gc-name-f">';
      if (NM) {
        var help = NAME_OPT
          ? (sampleName ? 'It replaces ' + sampleName + ' on the front. Leave it blank to leave the name off.' : 'Leave it blank for the design as pictured.')
          : 'It replaces ' + (sampleName || 'the sample name') + ' on the front.';
        h += '<label class="gc-lbl" for="gc-dogname">' + esc(W.nameLabel || 'Your dog\'s name') +
          (NAME_OPT ? ' <span class="gc-lbl-v">optional</span>' : ' <span class="gc-req">required</span>') + '</label>' +
          '<div class="gc-inp-row"><input class="gc-inp" id="gc-dogname" name="gc-dogname" maxlength="' + NAME_MAX + '" autocomplete="off" autocapitalize="words" spellcheck="false"' +
          ' placeholder="Type the name" value="' + esc(S.dogName) + '" aria-required="' + !NAME_OPT + '" aria-describedby="gc-name-help">' +
          '<span class="gc-count" id="gc-name-count"></span></div>' +
          '<p class="gc-note" id="gc-name-help">' + esc(help) + '</p>' +
          '<p class="gc-name-rule">Up to ' + NAME_MAX + ' letters. Spaces, apostrophes and hyphens are fine.</p>';
      }
      if (EX) {
        var num = X.kind === 'year' || X.kind === 'number';
        h += '<div class="gc-x-f' + (NM ? ' gc-x-sub' : '') + '"><label class="gc-lbl" for="gc-extra">' + esc(X.label) + ' <span class="gc-req">required</span></label>' +
          '<div class="gc-inp-row"><input class="gc-inp" id="gc-extra" name="gc-extra" maxlength="' + X.max + '" autocomplete="off" spellcheck="false"' +
          (num ? ' inputmode="numeric"' : X.kind === 'text' ? ' autocapitalize="words"' : '') +
          ' placeholder="' + esc('e.g. ' + extraSample()) + '" value="' + esc(S.extra) + '" aria-required="true" aria-describedby="gc-extra-help">' +
          '<span class="gc-count" id="gc-extra-count"></span></div>' +
          '<p class="gc-note" id="gc-extra-help">' + esc(X.sample ? 'It replaces ' + X.sample + ' on the front.' : 'It goes on the front.') + '</p>' +
          '<p class="gc-name-rule">' + esc(extraRule()) + '</p></div>';
      }
      return h + '</div><div class="gc-fob-w" aria-hidden="true"><div class="gc-fob gc-fob-x" id="gc-fob"><span class="gc-fob-hole"></span></div>' +
        '<p class="gc-prev-c">Front lettering</p></div></div></div>';
    }

    function updateX() {
      var fob = root.querySelector('#gc-fob'); if (!fob) return;
      var sw = swatch(S.color) || swatch(pictured || '') || swatch('Arizona');
      fob.style.backgroundImage = sw ? 'url(' + sw + ')' : '';
      var gold = S.finish === 'Gold foil' ? ' gold' : '';
      fob.querySelectorAll('.gc-fob-n, .gc-fob-l').forEach(function (e) { fob.removeChild(e); });
      var nameShown = S.dogName || sampleName || 'Name', extraShown = EX ? (S.extra || extraSample()) : '';
      var first = null;
      tplText().split(' / ').forEach(function (line, i) {
        var el = document.createElement('span');
        el.className = (i === 0 ? 'gc-fob-n' : 'gc-fob-l') + gold;
        line.split(/(\{(?:name|NAME|extra)\})/).forEach(function (part) {
          if (!part) return;
          var tk = /^\{(name|NAME|extra)\}$/.exec(part);
          if (!tk) { el.appendChild(document.createTextNode(part)); return; }
          var sp = document.createElement('span'), typed = tk[1] === 'extra' ? S.extra : S.dogName;
          var val = tk[1] === 'extra' ? extraShown : nameShown;
          sp.textContent = tk[1] === 'NAME' ? val.toUpperCase() : val;
          sp.className = 'gc-fob-t' + (typed ? '' : ' gc-ph-x');
          el.appendChild(sp);
        });
        if (i === 0) { el.id = 'gc-fob-n'; first = el; } else if (i === 1) el.id = 'gc-fob-l';
        fob.appendChild(el);
      });
      if (first) {
        // a template's first line can be longer than a name ("Team Jonesworth"): keep shrinking past the name floor
        fitName(first);
        var fs2 = parseFloat(first.style.fontSize) || 15;
        while (fs2 > 11 && first.scrollWidth > first.clientWidth + 1) { fs2 -= 1; first.style.fontSize = fs2 + 'px'; }
      }
      // caps lines stay on one line: tighten the tracking first, then the size
      fob.querySelectorAll('.gc-fob-l').forEach(function (el) {
        var ls = 1.6, sz = 8.5; el.style.letterSpacing = ls + 'px'; el.style.fontSize = sz + 'px';
        while (el.scrollWidth > el.clientWidth + 1 && (ls > 0.4 || sz > 6)) {
          if (ls > 0.4) ls = Math.round((ls - 0.3) * 10) / 10; else sz -= 0.5;
          el.style.letterSpacing = ls + 'px'; el.style.fontSize = sz + 'px';
        }
      });
      var nc = root.querySelector('#gc-name-count'); if (nc) nc.textContent = S.dogName.length + '/' + NAME_MAX;
      var xc = root.querySelector('#gc-extra-count'); if (xc) xc.textContent = X.kind === 'text' ? S.extra.length + '/' + X.max : '';
    }

    function blkColor() {
      var h = '<div class="gc-blk"><div class="gc-lbl">Leather <span class="gc-lbl-v" id="gc-color-name"></span></div><div class="gc-sws">';
      var list = colorIsVariant ? colorVals : [pictured || 'As pictured'].concat(LEATHERS.map(function (l) { return l[1]; }).filter(function (n) { return n !== pictured; }));
      list.forEach(function (n, i) {
        var img = swatch(n), asPic = !colorIsVariant && i === 0;
        h += '<button type="button" class="gc-sw' + (S.color === n ? ' on' : '') + (asPic && !img ? ' gc-sw-pic' : '') + '" data-c="' + esc(n) + '" title="' + esc(n) + (asPic ? ' (as pictured)' : '') + '"' +
          (img ? ' style="background-image:url(' + img + ')"' : '') + '>' + (asPic && !img ? '★' : '') + '</button>';
        if (asPic) h += '<span class="gc-sw-div"></span>';
      });
      h += '</div>';
      if (!colorIsVariant) h += '<p class="gc-note">Shown in the photos first. Every piece is cut to order, so any of our 17 leathers is yours for the asking.</p>';
      return h + '</div>';
    }

    function blkCards(o) {
      var info = (T.optInfo || {})[o.name] || {};
      var h = '<div class="gc-blk"><div class="gc-lbl">' + esc(o.name) + '</div><div class="gc-cards" data-o="' + esc(o.name) + '">';
      o.values.forEach(function (val) {
        h += '<button type="button" class="gc-card' + (S.opt[o.name] === val ? ' on' : '') + '" data-v="' + esc(val) + '">' +
          '<span class="gc-rd"><span></span></span><span class="gc-card-t"><b>' + esc(val) + '</b>' +
          (info[val] ? '<small>' + esc(info[val]) + '</small>' : '') + '</span><span class="gc-delta" data-o="' + esc(o.name) + '" data-v="' + esc(val) + '"></span></button>';
      });
      h += '</div>';
      if (/size/i.test(o.name) && hasSlide(/size/i)) h += '<button type="button" class="gc-link" data-slide="size">See the size guide →</button>';
      return h + '</div>';
    }

    function blkReq(r) {
      var h = '<div class="gc-blk"><div class="gc-lbl">' + esc(r.k) + (r.select ? ' <span class="gc-req">required</span>' : '') + '</div>';
      if (r.select) {
        h += '<select class="gc-select" data-r="' + esc(r.k) + '"><option value="">Choose your model</option>';
        r.select.forEach(function (m) { h += '<option' + (S.req[r.k] === m ? ' selected' : '') + '>' + esc(m) + '</option>'; });
        h += '</select>';
      } else {
        h += '<div class="gc-seg" data-r="' + esc(r.k) + '">';
        r.vals.forEach(function (v) { h += '<button type="button" class="' + (S.req[r.k] === v ? 'on' : '') + '" data-v="' + esc(v) + '">' + esc(v) + '</button>'; });
        h += '</div>';
      }
      return h + '</div>';
    }

    function blkFinish() {
      var F = [['As pictured', 'Exactly as in the photos'], ['Blind deboss', 'Tone-on-tone, pressed into the hide'], ['Gold foil', 'Warm gold pressed into the hide']];
      var h = '<div class="gc-blk"><div class="gc-lbl">' + esc(W.finishLabel || 'Verse finish') + '</div><div class="gc-seg gc-seg-3" id="gc-finish">';
      F.forEach(function (f) { h += '<button type="button" class="' + (S.finish === f[0] ? 'on' : '') + '" data-v="' + f[0] + '"><b>' + f[0] + '</b><small>' + f[1] + '</small></button>'; });
      return h + '</div></div>';
    }

    function blkPaper() {
      var h = '<div class="gc-blk"><div class="gc-lbl">Pages</div><div class="gc-seg" id="gc-paper">';
      ['Lined', 'Plain'].forEach(function (p) { h += '<button type="button" class="' + (S.paper === p ? 'on' : '') + '" data-v="' + p + '">' + p + '</button>'; });
      return h + '</div></div>';
    }

    function blkPers() {
      var p = T.pers;
      var h = '<div class="gc-blk gc-pers' + (S.persOn ? ' open' : '') + '" id="gc-pers">' +
        '<button type="button" class="gc-pers-hd" id="gc-pers-tg" aria-expanded="' + S.persOn + '">' +
        '<span class="gc-pers-t"><b>' + esc(p.title) + '</b><small>Optional · ' + esc(p.where) + '</small></span>' +
        '<span class="gc-fee" id="gc-pers-fee"></span><span class="gc-sw-tg"><span></span></span></button>' +
        '<div class="gc-pers-bd"><p class="gc-note">' + esc(p.hint) + '</p>' +
        '<div class="gc-inp-row"><input class="gc-inp" id="gc-text" maxlength="' + p.max + '" placeholder="' + esc(p.ph) + '" value="' + esc(S.text) + '">' +
        '<span class="gc-count" id="gc-count"></span></div>' +
        '<div class="gc-sym"><span>Add</span><button type="button" data-s="♥">♥</button><button type="button" data-s="&amp;">&amp;</button><button type="button" data-s="·">·</button></div>' +
        '<div class="gc-seg" id="gc-font"><button type="button" class="' + (S.font === 'Straight' ? 'on' : '') + '" data-v="Straight"><span class="gc-f-st">Straight</span></button>' +
        '<button type="button" class="' + (S.font === 'Cursive' ? 'on' : '') + '" data-v="Cursive"><span class="gc-f-cu">Cursive</span></button></div>' +
        '<div class="gc-prev" id="gc-prev"><span id="gc-prev-t"></span></div>' +
        '<p class="gc-prev-c">Preview of the lettering. Stamped in the same finish as the ' + esc(W.finishNoun || 'verse') + '.</p></div></div>';
      return h;
    }

    function blkDeadline() {
      return '<div class="gc-blk gc-dl"><div class="gc-lbl">Gift date <span class="gc-lbl-v">optional</span></div>' +
        '<input class="gc-inp" id="gc-deadline" placeholder="Need it by a certain day? Tell us, e.g. Dec 18" value="' + esc(S.deadline) + '"></div>';
    }

    function render() {
      var h = head();
      if (XM) h += blkX(); else if (NM) h += blkName();
      h += blkColor();
      cardOpts.forEach(function (o) { h += blkCards(o); });
      (T.req || []).forEach(function (r) { h += blkReq(r); });
      if (T.paper) h += blkPaper();
      h += blkFinish();
      if (T.pers) h += blkPers();
      h += blkDeadline();
      h += '<div class="gc-sum"><div class="gc-sum-r"><span>Total</span><span class="gc-sum-p" id="gc-total"></span></div>' +
        '<p class="gc-err" id="gc-err"></p>' +
        '<button type="button" class="gc-atc" id="gc-atc">Add to cart</button>' +
        '<p class="gc-dn">Handmade to order. 10% comes off automatically in the cart.</p></div>';
      h += '<div class="gc-ct"><p><b>A question before you order?</b> Ask for a mockup of your lettering and we will send one.</p>' +
        '<div class="gc-ct-bs"><button type="button" id="gc-chat">Live chat</button><button type="button" id="gc-eml">Copy email</button></div></div>';

      var wrap = document.createElement('div');
      wrap.className = 'gc-body';
      wrap.innerHTML = h;
      var old = root.querySelector('.gc-body');
      if (old) old.parentNode.replaceChild(wrap, old); else root.appendChild(wrap);
      bind(wrap);
      update();
    }

    // ---------- state -> DOM ----------
    function update() {
      var v = findVariant();
      var pe = root.querySelector('#gc-price'), te = root.querySelector('#gc-total');
      if (v) { pe.textContent = money(v.price); te.textContent = money(v.price); }
      else { pe.textContent = 'Unavailable'; te.textContent = '—'; }

      var cn = root.querySelector('#gc-color-name');
      if (cn) cn.textContent = S.color + (!colorIsVariant && S.color === (pictured || 'As pictured') && pictured ? ' · as pictured' : '');
      root.querySelectorAll('.gc-sw').forEach(function (b) { b.classList.toggle('on', b.getAttribute('data-c') === S.color); });

      // price deltas on cards, relative to the current selection
      root.querySelectorAll('.gc-delta').forEach(function (d) {
        var o = {}; o[d.getAttribute('data-o')] = d.getAttribute('data-v');
        var alt = findVariant(o);
        if (!v || !alt) { d.textContent = ''; return; }
        var diff = alt.price - v.price;
        d.textContent = diff === 0 ? (S.opt[d.getAttribute('data-o')] === d.getAttribute('data-v') ? '' : 'same price') : (diff > 0 ? '+' : '−') + money(Math.abs(diff));
      });

      var fee = root.querySelector('#gc-pers-fee');
      if (fee) {
        if (persOpt) {
          var yes = findVariant(obj(persOpt, 'Yes')), no = findVariant(obj(persOpt, 'No'));
          fee.textContent = yes && no ? '+' + money(yes.price - no.price) : '';
        } else fee.textContent = 'Free';
      }
      var pers = root.querySelector('#gc-pers');
      if (pers) {
        pers.classList.toggle('open', S.persOn);
        root.querySelector('#gc-pers-tg').setAttribute('aria-expanded', S.persOn);
        root.querySelector('#gc-count').textContent = S.text.length + '/' + T.pers.max;
        var pv = root.querySelector('#gc-prev'), pt = root.querySelector('#gc-prev-t');
        pt.textContent = S.text || (T.pers.ph.split(' · ')[0]);
        pt.className = (S.font === 'Cursive' ? 'gc-f-cu' : 'gc-f-st') + (S.text ? '' : ' gc-prev-ph') + (S.finish === 'Gold foil' ? ' gold' : '');
        var sw = swatch(S.color) || swatch(pictured || '') || swatch('Arizona');
        pv.style.backgroundImage = sw ? 'url(' + sw + ')' : '';
      }
      if (XM) updateX(); else if (NM) updateName();
      if (v && window.history && history.replaceState) {
        try { var u = new URL(location.href); u.searchParams.set('variant', v.id); history.replaceState(null, '', u.toString()); } catch (e) {}
      }
    }
    function obj(k, v) { var o = {}; o[k] = v; return o; }

    // ---------- gallery sync ----------
    function slides() { return Array.prototype.slice.call(document.querySelectorAll('.product-main-slider .swiper-slide:not(.swiper-slide-duplicate)')); }
    function hasSlide(re) { return slides().some(function (s) { return re.test(s.getAttribute('data-alt') || ''); }); }
    function goSlide(re) {
      var list = slides(), idx = -1;
      for (var i = 0; i < list.length; i++) if (re.test(list[i].getAttribute('data-alt') || '')) { idx = i; break; }
      if (idx !== -1 && window.GC_SWIPER) window.GC_SWIPER.slideToLoop(idx);
    }

    // ---------- events ----------
    function bind(w) {
      w.querySelectorAll('.gc-sw').forEach(function (b) {
        b.addEventListener('click', function () {
          S.color = b.getAttribute('data-c');
          if (colorIsVariant) goSlide(new RegExp('\\b' + S.color.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\b', 'i'));
          update();
        });
      });
      w.querySelectorAll('.gc-cards').forEach(function (g) {
        var on = g.getAttribute('data-o');
        g.querySelectorAll('.gc-card').forEach(function (b) {
          b.addEventListener('click', function () {
            S.opt[on] = b.getAttribute('data-v');
            g.querySelectorAll('.gc-card').forEach(function (x) { x.classList.toggle('on', x === b); });
            update();
          });
        });
      });
      w.querySelectorAll('.gc-seg[data-r]').forEach(function (g) {
        g.querySelectorAll('button').forEach(function (b) {
          b.addEventListener('click', function () {
            S.req[g.getAttribute('data-r')] = b.getAttribute('data-v');
            g.querySelectorAll('button').forEach(function (x) { x.classList.toggle('on', x === b); });
          });
        });
      });
      w.querySelectorAll('.gc-select').forEach(function (s) {
        s.addEventListener('change', function () { S.req[s.getAttribute('data-r')] = s.value; s.classList.remove('bad'); err(''); });
      });
      segState(w, '#gc-finish', function (v) { S.finish = v; });
      segState(w, '#gc-paper', function (v) { S.paper = v; });
      segState(w, '#gc-font', function (v) { S.font = v; });

      var tg = w.querySelector('#gc-pers-tg');
      if (tg) tg.addEventListener('click', function () {
        S.persOn = !S.persOn; update();
        if (S.persOn) setTimeout(function () { var i = w.querySelector('#gc-text'); if (i) i.focus(); }, 180);
      });
      var ti = w.querySelector('#gc-text');
      if (ti) ti.addEventListener('input', function () { S.text = ti.value; ti.classList.remove('bad'); err(''); update(); });
      w.querySelectorAll('.gc-sym button').forEach(function (b) {
        b.addEventListener('click', function () {
          if (!ti || ti.value.length >= T.pers.max) return;
          var s = b.getAttribute('data-s'), a = ti.selectionStart == null ? ti.value.length : ti.selectionStart;
          ti.value = ti.value.slice(0, a) + s + ti.value.slice(a); S.text = ti.value; ti.focus(); update();
        });
      });
      var dn = w.querySelector('#gc-dogname');
      if (dn) dn.addEventListener('input', function () {
        var raw = dn.value, c = cleanName(raw);
        if (c !== raw) {
          // keep the caret where the shopper was typing
          var pos = dn.selectionStart == null ? raw.length : dn.selectionStart;
          var kept = cleanName(raw.slice(0, pos)).length;
          dn.value = c;
          try { dn.setSelectionRange(Math.min(kept, c.length), Math.min(kept, c.length)); } catch (e) {}
        }
        S.dogName = c;
        dn.classList.remove('bad'); dn.removeAttribute('aria-invalid'); err(''); update();
      });
      if (dn) dn.addEventListener('blur', function () {
        var t = cleanName(dn.value).replace(/[\s'-]+$/, '');
        if (t !== dn.value) { dn.value = t; S.dogName = t; update(); }
      });
      var xi = w.querySelector('#gc-extra');
      if (xi) xi.addEventListener('input', function () {
        var raw = xi.value, c = cleanExtra(raw);
        if (c !== raw) {
          var pos = xi.selectionStart == null ? raw.length : xi.selectionStart;
          var kept = cleanExtra(raw.slice(0, pos)).length;
          xi.value = c;
          try { xi.setSelectionRange(Math.min(kept, c.length), Math.min(kept, c.length)); } catch (e) {}
        }
        S.extra = c;
        xi.classList.remove('bad'); xi.removeAttribute('aria-invalid'); err(''); update();
      });
      if (xi) xi.addEventListener('blur', function () {
        var t = finalExtra(xi.value) || cleanExtra(xi.value).replace(/[ ·]+$/, '');
        if (t !== xi.value) { xi.value = t; S.extra = t; update(); }
      });
      var dl = w.querySelector('#gc-deadline');
      if (dl) dl.addEventListener('input', function () { S.deadline = dl.value; });
      w.querySelectorAll('.gc-link[data-slide]').forEach(function (b) { b.addEventListener('click', function () { goSlide(/size/i); }); });

      w.querySelector('#gc-atc').addEventListener('click', addToCart);
      var ce = w.querySelector('#gc-eml');
      ce.addEventListener('click', function () {
        (navigator.clipboard ? navigator.clipboard.writeText(EM) : Promise.reject()).then(function () {
          ce.textContent = 'Copied'; setTimeout(function () { ce.textContent = 'Copy email'; }, 2000);
        }, function () { window.location.href = 'mailto:' + EM; });
      });
      w.querySelector('#gc-chat').addEventListener('click', function () {
        if (window.Tidio) { Tidio.open(); return; } if (window.$crisp) { $crisp.push(['do', 'chat:open']); return; }
        if (window.Intercom) { Intercom('show'); return; } if (window.zE) { zE('messenger', 'open'); return; }
        window.location.href = 'mailto:' + EM;
      });
    }
    function segState(w, sel, set) {
      var g = w.querySelector(sel); if (!g) return;
      g.querySelectorAll('button').forEach(function (b) {
        b.addEventListener('click', function () {
          set(b.getAttribute('data-v'));
          g.querySelectorAll('button').forEach(function (x) { x.classList.toggle('on', x === b); });
          update();
        });
      });
    }
    function err(m) { var e = root.querySelector('#gc-err'); if (e) { e.textContent = m; e.style.display = m ? 'block' : 'none'; } }

    // ---------- add to cart ----------
    function addToCart() {
      var v = findVariant();
      if (!v) { err('That combination is not available. Please contact us.'); return; }
      var dogName = NM ? cleanName(S.dogName).replace(/[\s'-]+$/, '') : '';
      if (NM && !(NAME_OPT && !dogName) && !nameOk(dogName)) {
        err('Type ' + (W.nameNoun || 'your dog\'s name') + '. It replaces ' + (sampleName || 'the sample name') + ' on the front.');
        var dn = root.querySelector('#gc-dogname');
        if (dn) {
          dn.classList.add('bad'); dn.setAttribute('aria-invalid', 'true'); dn.focus();
          if (dn.scrollIntoView) dn.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
        return;
      }
      var extra = EX ? finalExtra(S.extra) : '';
      if (EX && !extra) {
        err(extraErr());
        var xi = root.querySelector('#gc-extra');
        if (xi) {
          xi.classList.add('bad'); xi.setAttribute('aria-invalid', 'true'); xi.focus();
          if (xi.scrollIntoView) xi.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
        return;
      }
      var missing = (T.req || []).filter(function (r) { return r.select && !S.req[r.k]; })[0];
      if (missing) {
        err('Please choose your ' + missing.k + '.');
        var s = root.querySelector('.gc-select[data-r="' + missing.k + '"]'); if (s) { s.classList.add('bad'); s.focus(); }
        return;
      }
      if (S.persOn && !S.text.trim()) {
        err('Type the lettering, or switch personalization off.');
        var ti = root.querySelector('#gc-text'); if (ti) { ti.classList.add('bad'); ti.focus(); }
        return;
      }
      var props = {};
      if (XM) {
        if (NM && dogName) props[W.nameProp || "Dog's Name"] = dogName;
        if (EX) props[X.label] = extra;
        props['Front Design'] = frontDesign(dogName, extra);
      } else if (NM) {
        props[W.nameProp || "Dog's Name"] = dogName;
        props['Front Design'] = secondLine ? dogName + ' / ' + secondLine : dogName;
      }
      if (!colorIsVariant) props['Leather Color'] = S.color === (pictured || 'As pictured') ? (pictured ? pictured + ' (as pictured)' : 'As pictured') : S.color + (pictured ? ' (instead of pictured ' + pictured + ')' : ' (instead of pictured)');
      (T.req || []).forEach(function (r) { props[r.k] = S.req[r.k]; });
      if (T.paper) props['Pages'] = S.paper;
      props[W.finishProp || 'Verse Finish'] = S.finish;
      if (T.pers && S.persOn) {
        props['Personalization'] = S.text.trim();
        props['Personalization Font'] = S.font;
        props['Personalization Placement'] = T.pers.where;
      }
      if (S.deadline.trim()) props['Gift Deadline'] = S.deadline.trim();

      var btn = root.querySelector('#gc-atc');
      btn.disabled = true; btn.textContent = 'Adding…'; err('');
      fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: v.id, quantity: 1, properties: props }] }) })
        .then(function (r) { if (!r.ok) throw new Error('add'); return fetch('/cart.js'); })
        .then(function (r) { return r.json(); })
        .then(function (c) {
          cartCount(c.item_count);
          btn.classList.add('ok'); btn.textContent = 'Added ✓';
          setTimeout(function () { btn.disabled = false; btn.classList.remove('ok'); btn.textContent = 'Add to cart'; }, 2200);
          modal(c.item_count);
        })
        .catch(function () { btn.disabled = false; btn.textContent = 'Add to cart'; err('Something went wrong. Please try again.'); });
    }

    function cartCount(n) {
      ['.cart-count', '.cart-link__bubble', '.cart-count-bubble', '[data-cart-count]', '.cart-link span', '.icon-cart-count',
        '.site-header__cart-count', '.header__cart-count', '#CartCount', '.cart-counter', '.cart__count',
        'a[href="/cart"] .count', '.header-cart-count', '.cart-link .count'].forEach(function (sel) {
        document.querySelectorAll(sel).forEach(function (el) { el.textContent = n; el.style.display = ''; el.classList.remove('hidden', 'hide'); });
      });
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { count: n } }));
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }

    function modal(n) {
      var ex = document.getElementById('gc-modal'); if (ex) ex.parentNode.removeChild(ex);
      var m = document.createElement('div');
      m.id = 'gc-modal'; m.className = 'gc-modal';
      m.innerHTML = '<div class="gc-modal-bg"></div><div class="gc-modal-card" role="dialog" aria-modal="true">' +
        '<div class="gc-modal-i">✓</div><h3>In your cart</h3>' +
        (window.GC_META && GC_META.phrase ? '<p class="gc-modal-q">“' + esc(GC_META.phrase) + '”</p>' : '') +
        '<p>Your cart now holds <b>' + n + ' item' + (n === 1 ? '' : 's') + '</b>.</p>' +
        '<div class="gc-modal-bs"><button type="button" class="gc-mb2" id="gc-m-c">Keep browsing</button>' +
        '<button type="button" class="gc-mb1" id="gc-m-g">Go to cart →</button></div></div>';
      document.body.appendChild(m);
      requestAnimationFrame(function () { m.classList.add('on'); });
      function close() { m.classList.remove('on'); setTimeout(function () { if (m.parentNode) m.parentNode.removeChild(m); }, 250); document.removeEventListener('keydown', k); }
      function k(e) { if (e.key === 'Escape') close(); }
      m.querySelector('.gc-modal-bg').addEventListener('click', close);
      m.querySelector('#gc-m-c').addEventListener('click', close);
      m.querySelector('#gc-m-g').addEventListener('click', function () { window.location.href = '/cart'; });
      document.addEventListener('keydown', k);
    }

    render();
  }

  function initSwiper() {
    if (window.GC_SWIPER || !document.querySelector('.gc-root .product-main-slider')) return;
    if (typeof Swiper === 'undefined') { setTimeout(initSwiper, 100); return; }
    var th = null;
    if (document.querySelector('.gc-root .product-thumb-slider')) {
      th = new Swiper('.gc-root .product-thumb-slider', { spaceBetween: 10, slidesPerView: 4, freeMode: true, watchSlidesProgress: true, breakpoints: { 640: { slidesPerView: 5 }, 768: { slidesPerView: 6 } } });
    }
    window.GC_SWIPER = new Swiper('.gc-root .product-main-slider', {
      spaceBetween: 10, loop: true,
      navigation: { nextEl: '.gc-root .swiper-button-next', prevEl: '.gc-root .swiper-button-prev' },
      pagination: { el: '.gc-root .swiper-pagination', clickable: true },
      thumbs: th ? { swiper: th } : undefined,
      zoom: { maxRatio: 3, minRatio: 1, toggle: true }
    });
  }

  function boot() { init(); initSwiper(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  window.addEventListener('load', boot);
})();
